import React from 'react';
import styled from 'styled-components';
import {Button, ButtonDropdown, DropdownList, EMPHASIS, FlexRow} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import type {QuoteContentBlock, SourceBlockState} from '../persistence/sourceBlockState';
import type {QuoteSectionType} from '../resolver/types';

/*
 * The top row of the editor's own box, not a bar floating above it: the design wraps the versions
 * row, the formatting toolbar and the editable in one bordered, rounded container, so this carries
 * only a bottom rule and the box's own top corners.
 */
const Bar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	min-width: 0;
	padding: 6px 8px;
	background: ${({theme}) => theme.sys.color.background.neutral['2']};
	border: 1px solid ${({theme}) => theme.sys.color.border.decorative};
	border-bottom: 0;
	border-radius: 4px 4px 0 0;
`;

/*
 * The bar is one row: the version picker, the status and Save. A long block name would otherwise
 * push the status and the Save button out of the bar entirely - a ~50-character name is enough -
 * so the name and the status each give way rather than the control the user came for.
 */
const PickerCell = styled.span`
	display: block;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const StatusNote = styled.span`
	${({theme}) => theme.sys.font.detail[1]}
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: ${({theme}) => theme.sys.color.text.content.auxiliary};
`;

const DefaultPill = styled.span`
	flex: none;
	padding: 1px 6px;
	margin-left: 6px;
	border-radius: 999px;
	background: ${({theme}) => theme.sys.color.background.neutral['2']};
	${({theme}) => theme.sys.font.detail[1]}
	color: ${({theme}) => theme.sys.color.text.content.auxiliary};
`;

type Props = {
	sectionType: QuoteSectionType;
	/** The section's display label, used to name what a Save-as dialog is about to save. */
	sectionLabel: string;
	blocks: readonly QuoteContentBlock[];
	state: SourceBlockState;
	selectedBlockId: string | null;
	onApply: (block: QuoteContentBlock) => void;
	onSave: (block: QuoteContentBlock) => void;
	onSaveAs: () => void;
	onSetDefault: (block: QuoteContentBlock) => void;
};

/**
 * The saved-content controls for one authored section.
 *
 * The dropdown's selection is driven by the section's stored provenance, never the other way
 * round: the section body is always authoritative, so a block that has been edited since — or
 * deleted — changes what this bar SAYS without touching the quote's copy.
 */
const ContentBlockBar: React.FC<Props> = ({
	sectionType,
	sectionLabel,
	blocks,
	state,
	selectedBlockId,
	onApply,
	onSave,
	onSaveAs,
	onSetDefault,
}) => {
	const intl = useIntl();
	const sectionBlocks = blocks.filter(block => block.sectionType === sectionType);
	// A missing block has no entry to resolve, which is why the label falls back to the invitation.
	const selected = sectionBlocks.find(block => block.id === selectedBlockId);

	// The trigger names the version in use; whether it is saved is the note's job, beside Save.
	const label = selected ? selected.name : intl.formatMessage({id: 'quote_designer.content_block.choose'});

	const statusNote =
		state === 'dirty'
			? intl.formatMessage({id: 'quote_designer.content_block.dirty'})
			: selected
			? intl.formatMessage({id: 'quote_designer.content_block.saved'}, {name: selected.name})
			: '';

	return (
		<Bar data-cy={`quote-content-block-bar-${sectionType}`}>
			<DropdownList>
				<DropdownList.Trigger>
					<ButtonDropdown emphasis={EMPHASIS.LOW} data-cy={`quote-content-block-picker-${sectionType}`}>
						<PickerCell title={label}>{label}</PickerCell>
					</ButtonDropdown>
				</DropdownList.Trigger>
				<DropdownList.Content>
					{sectionBlocks.map(block => (
						<DropdownList.Item key={block.id} onClick={() => onApply(block)}>
							{block.name}
							{block.isDefault ? (
								<DefaultPill>
									{intl.formatMessage({id: 'quote_designer.content_block.default_pill'})}
								</DefaultPill>
							) : null}
						</DropdownList.Item>
					))}
					{/* Saving under a new name and promoting one to the account default are choices
					    ABOUT the saved content, so the design keeps them in its menu rather than
					    spending bar space on two more buttons. */}
					<DropdownList.Item onClick={onSaveAs} data-cy={`quote-content-block-save-as-${sectionType}`}>
						{intl.formatMessage({id: 'quote_designer.content_block.save_as'})}
					</DropdownList.Item>
					<DropdownList.Item
						disabled={!selected || selected.isDefault}
						onClick={() => selected && onSetDefault(selected)}
						data-cy={`quote-content-block-set-default-${sectionType}`}
					>
						{intl.formatMessage({id: 'quote_designer.content_block.set_default'})}
					</DropdownList.Item>
				</DropdownList.Content>
			</DropdownList>

			<FlexRow gap="s" alignItems="center">
				<StatusNote data-cy={`quote-content-block-status-${sectionType}`} title={statusNote}>
					{statusNote}
				</StatusNote>
				{/*
				 * With a version in use, Save overwrites it, and there is nothing to overwrite until the
				 * copy has moved away from it. With none in use — which is where an account with no
				 * saved content starts — Save names and creates the first one instead of sitting
				 * permanently disabled, since there is always something worth saving.
				 */}
				<Button
					emphasis={!selected || state === 'dirty' ? EMPHASIS.MEDIUM : EMPHASIS.LOW}
					disabled={Boolean(selected) && state !== 'dirty'}
					onPress={() => (selected ? onSave(selected) : onSaveAs())}
					// The section name alone would REPLACE the button's own text as its accessible name,
					// so a screen reader announced "Terms & Conditions, button" for a control that
					// overwrites saved copy. The action has to stay in the name; the section is what
					// tells three identical Save buttons apart.
					aria-label={intl.formatMessage({id: 'quote_designer.content_block.save_section'}, {section: sectionLabel})}
					data-cy={`quote-content-block-save-${sectionType}`}
				>
					{intl.formatMessage({id: 'quote_designer.content_block.save'})}
				</Button>
			</FlexRow>
		</Bar>
	);
};

export default ContentBlockBar;
